import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import useSEO from "../hooks/useSEO";
import { CheckCircle2 } from "lucide-react";

const ABOUT_IMG =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/4694gjo3_image.png";

const CAPABILITIES = [
  "Fire alarm systems",
  "Emergency lighting",
  "Fire extinguishers",
  "Testing and certification",
  "Planned maintenance",
  "Fault finding and repairs",
  "Commercial and industrial support",
  "Professional advice",
  "Ongoing customer support",
];

export default function About() {
  useSEO({
    title: "About Us | Fire Safety Systems Ireland",
    description:
      "Fire Safety Systems delivers complete fire safety solutions across Ireland — alarm systems, emergency lighting, extinguishers, testing, certification and planned maintenance.",
    canonicalPath: "/about",
  });

  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="A trusted partner in fire safety across Ireland."
        subtitle="Fire Safety Systems delivers complete fire safety solutions to commercial, industrial and public-sector clients throughout Ireland — from initial surveys and design through to installation, testing, servicing and maintenance."
        image={ABOUT_IMG}
        imageAlt="A commercial premises supported by Fire Safety Systems"
        ctaPrimary={{ to: "/contact", label: "Get a Free Quote" }}
        ctaSecondary={{ to: "/services/fire-alarms", label: "View Our Services" }}
      />

      <section className="fss-section bg-white">
        <div className="fss-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="fss-overline">Our purpose</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Fire safety is about protecting people, property and business
              continuity.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-slate-600 leading-relaxed">
            <p>
              Fire safety is not just about meeting legal requirements — it is
              about protecting people, property and business continuity.
            </p>
            <p>
              From initial surveys and system design through to installation,
              commissioning, testing, servicing and maintenance, Fire Safety
              Systems supports customers throughout the full lifecycle of their
              fire safety systems.
            </p>
            <p>
              We work with businesses, property managers and contractors across
              Ireland to deliver dependable fire protection tailored to each
              premises, backed by professional advice and outstanding customer
              service.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 fss-section border-y border-slate-200">
        <div className="fss-container">
          <div className="max-w-2xl mb-10">
            <div className="fss-overline">What we do</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              A complete fire safety capability
            </h2>
            <p className="mt-3 text-slate-600">
              Our team supports customers across the full lifecycle of their
              fire safety systems.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {CAPABILITIES.map((c, i) => (
              <div
                key={i}
                className="bg-white p-6 flex items-start gap-3"
                data-testid={`about-capability-${i}`}
              >
                <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0 mt-0.5" />
                <span className="text-slate-800 font-medium">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Looking for a long-term fire safety partner?"
        subtitle="Talk to our team about your premises and how we can support your fire safety systems for the long run."
        primary={{ to: "/contact", label: "Get a Free Quote" }}
        secondary={{ to: "/industries", label: "Industries We Support" }}
      />
    </>
  );
}
